import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import {
  Form,
  Button,
  Alert,
  Container,
  Row,
  Col,
  Card,
} from 'react-bootstrap';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import logo from '../assets/LogoNavbar.png';

export default function ResetPassword() {
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const [validSession, setValidSession] = useState(false);
  const [verifying, setVerifying] = useState(true);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    const verifyToken = async () => {
      const tokenHash = searchParams.get('token_hash');
      const type = searchParams.get('type');

      if (!tokenHash || type !== 'recovery') {
        setError('Invalid or missing reset link. Please request a new one.');
        setVerifying(false);
        return;
      }

      const { error } = await supabase.auth.verifyOtp({
        token_hash: tokenHash,
        type: 'recovery',
      });

      if (error) {
        setError(
          'This reset link is invalid or has expired. Please request a new one.',
        );
      } else {
        setValidSession(true);
      }

      setVerifying(false);
    };

    verifyToken();
  }, [searchParams]);

  const handleReset = async (e: React.FormEvent) => {
    e.preventDefault();
    if (password !== confirm) {
      setError('Passwords do not match.');
      return;
    }
    if (password.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }

    setLoading(true);
    setError('');

    const { error } = await supabase.auth.updateUser({ password });

    setLoading(false);

    if (error) {
      setError(error.message);
    } else {
      setSuccess(true);
      setTimeout(() => navigate('/login'), 3000);
    }
  };

  return (
    <div className="bg-light min-vh-100 d-flex align-items-center">
      <Container className="py-5">
        <Card className="shadow-lg rounded-3 overflow-hidden">
          <Row className="g-0">
            <Col
              md={6}
              className="bg-primary text-white p-4 d-flex flex-column justify-content-center align-items-center text-center"
            >
              <div>
                <img
                  src={logo}
                  alt="SupplyPassport Logo"
                  height="80"
                  className="mb-4"
                />
                <h2 className="fw-bold">Set a new password</h2>
                <p className="mt-3 mb-0">
                  Choose something strong and memorable.
                </p>
              </div>
            </Col>

            <Col md={6} className="bg-white p-4">
              <div className="mb-4">
                <h2 className="h4 fw-bold mb-1">New password</h2>
              </div>

              {verifying ? (
                <p className="text-muted">Verifying your reset link...</p>
              ) : success ? (
                <Alert variant="success">
                  Password updated! Redirecting you to login...
                </Alert>
              ) : !validSession ? (
                <Alert variant="warning">
                  {error} <Link to="/forgot-password">Request a new link</Link>.
                </Alert>
              ) : (
                <>
                  {error && <Alert variant="danger">{error}</Alert>}
                  <Form onSubmit={handleReset} className="mb-3">
                    <Form.Group className="mb-3" controlId="formPassword">
                      <Form.Label className="small">New password</Form.Label>
                      <div className="input-group">
                        <span className="input-group-text bg-white border-end-0">
                          <i className="bi bi-lock" />
                        </span>
                        <Form.Control
                          type="password"
                          placeholder="••••••••"
                          className="border-start-0"
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          required
                          autoFocus
                        />
                      </div>
                    </Form.Group>

                    <Form.Group className="mb-3" controlId="formConfirm">
                      <Form.Label className="small">
                        Confirm password
                      </Form.Label>
                      <div className="input-group">
                        <span className="input-group-text bg-white border-end-0">
                          <i className="bi bi-lock-fill" />
                        </span>
                        <Form.Control
                          type="password"
                          placeholder="••••••••"
                          className="border-start-0"
                          value={confirm}
                          onChange={(e) => setConfirm(e.target.value)}
                          required
                        />
                      </div>
                    </Form.Group>

                    <Button
                      type="submit"
                      className="w-100 btn-primary my-4"
                      disabled={loading}
                    >
                      {loading ? 'Updating...' : 'Update password'}
                    </Button>
                  </Form>
                </>
              )}
            </Col>
          </Row>
        </Card>
      </Container>
    </div>
  );
}
